<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { SelectOption } from 'naive-ui';
import type { SigningProvider } from '@/api';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCard from '@/components/design/base/GaiaCard.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { signingProviderMap } from '@/components/design/statusMaps';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useOrganizationStore } from '@/stores/organizationStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { MAX_EXPIRY_DAYS_AHEAD } from '@/composables/useSigningWizard';

/**
 * The active organisation's own settings, for its admins (the route requires
 * org admin): its name and description, and the signing provider one-click
 * sends use. Credits and billing stay with the platform admins.
 */
const message = useGaiaToast();
const organizationStore = useOrganizationStore();
const organisation = computed(() => organizationStore.activeOrganization);
if (!organisation.value) void organizationStore.fetchActiveOrganization();

// ── General ─────────────────────────────────────────────────────────────────
const name = ref('');
const description = ref('');
const savingGeneral = ref(false);

// Seeded from the store, and re-seeded when it changes (a switch, a save).
watch(
  organisation,
  value => {
    name.value = value?.name ?? '';
    description.value = value?.description ?? '';
  },
  { immediate: true }
);

const nameError = computed(() => (name.value.trim() ? undefined : 'Please enter a name'));
const generalDirty = computed(
  () =>
    name.value.trim() !== (organisation.value?.name ?? '') ||
    description.value.trim() !== (organisation.value?.description ?? '')
);

async function saveGeneral(): Promise<void> {
  if (nameError.value || !generalDirty.value) return;
  savingGeneral.value = true;
  try {
    // An empty description is sent as "" — null would mean "leave it as it is".
    await organizationStore.updateActiveOrganization({
      name: name.value.trim(),
      description: description.value.trim(),
    });
    message.success('Organisation updated.');
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to update the organisation.'));
  } finally {
    savingGeneral.value = false;
  }
}

function resetGeneral(): void {
  name.value = organisation.value?.name ?? '';
  description.value = organisation.value?.description ?? '';
}

// ── Signing ─────────────────────────────────────────────────────────────────
// No default means one-click sends are off; clearing the select removes it. A
// stored default the org can no longer use (its integration was disconnected)
// is ignored by the backend, so it reads as unset here too.
const savingProvider = ref(false);

const availableProviders = computed<SigningProvider[]>(
  () => organisation.value?.available_signing_providers ?? []
);

const providerOptions = computed(() =>
  availableProviders.value.map(provider => ({
    label: signingProviderMap[provider],
    value: provider,
  }))
);

const providerValue = computed<SigningProvider | null>(() => {
  const provider = organisation.value?.default_signing_provider;
  return provider && availableProviders.value.includes(provider) ? provider : null;
});

async function changeProvider(value: SigningProvider | null): Promise<void> {
  savingProvider.value = true;
  try {
    await organizationStore.updateActiveOrganization({ default_signing_provider: value });
    message.success('Default signing provider saved.');
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to set the default signing provider.'));
  } finally {
    savingProvider.value = false;
  }
}

// How long a new signing request runs: prefilled as its expiry, still changeable per request.
const savingExpiration = ref(false);

// The longest preset is the provider's limit: an expiry at most a year out (see MAX_EXPIRY_DAYS_AHEAD).
const EXPIRATION_PRESETS = [7, 14, 30, 60, 90, 180, MAX_EXPIRY_DAYS_AHEAD];
// A value saved before (or by the API) that isn't a preset stays pickable.
const expirationOptions = computed(() => {
  const current = organisation.value?.signing_expiration_days;
  const days =
    current && !EXPIRATION_PRESETS.includes(current)
      ? [...EXPIRATION_PRESETS, current].sort((a, b) => a - b)
      : EXPIRATION_PRESETS;
  return days.map(value => ({ label: daysLabel(value), value }));
});

function daysLabel(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * A number typed into the select becomes its own option; anything else becomes a
 * disabled one saying why. Its label repeats what was typed: the select keeps only
 * options whose label contains the input, so a reason without it would be filtered
 * away and the menu would read "No Data".
 */
function createExpirationOption(input: string): SelectOption {
  const typed = input.trim();
  const days = Number(typed);
  if (Number.isInteger(days) && days >= 1 && days <= MAX_EXPIRY_DAYS_AHEAD) {
    return { label: daysLabel(days), value: days };
  }
  const reason =
    Number.isInteger(days) && days > MAX_EXPIRY_DAYS_AHEAD
      ? `${typed} days is over the limit — at most ${MAX_EXPIRY_DAYS_AHEAD} days (one year)`
      : `${typed} isn’t a valid number of days — type 1 to ${MAX_EXPIRY_DAYS_AHEAD}`;
  return { label: reason, value: 'invalid', disabled: true };
}

async function changeExpirationDays(value: number | string | null): Promise<void> {
  if (typeof value !== 'number' || value === organisation.value?.signing_expiration_days) return;
  savingExpiration.value = true;
  try {
    await organizationStore.updateActiveOrganization({ signing_expiration_days: value });
    message.success('Default expiry saved.');
  } catch (error) {
    message.error(extractErrorDetail(error, 'Failed to save the default expiry.'));
  } finally {
    savingExpiration.value = false;
  }
}
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.page">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.general }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="16" :wrap="false">
      <n-flex vertical :size="4" :wrap="false">
        <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
          {{ navLabels.general }}
        </gaia-header>
        <gaia-text size="body" tone="tertiary">
          How your organisation is named and how it sends documents for signing.
        </gaia-text>
      </n-flex>

      <n-spin :show="!organisation && organizationStore.loading">
        <n-flex v-if="organisation" vertical :size="16" :wrap="false">
          <gaia-card>
            <template #header>Details</template>
            <n-flex vertical :size="16" :wrap="false">
              <gaia-form-field label="Name" required :error="nameError">
                <gaia-input
                  v-model:value="name"
                  placeholder="Organisation name"
                  :maxlength="255"
                  @keydown.enter="saveGeneral"
                />
              </gaia-form-field>
              <gaia-form-field label="Description" hint="Optional">
                <gaia-textarea
                  v-model:value="description"
                  placeholder="What this organisation is for"
                  :autosize="{ minRows: 3, maxRows: 8 }"
                />
              </gaia-form-field>
              <gaia-text size="meta" tone="muted"
                >Identifier: {{ organisation.identifier }}</gaia-text
              >
            </n-flex>
            <template #action>
              <n-flex justify="end" :size="8">
                <gaia-button
                  size="small"
                  quaternary
                  :disabled="!generalDirty || savingGeneral"
                  @click="resetGeneral"
                >
                  Discard
                </gaia-button>
                <gaia-button
                  size="small"
                  type="primary"
                  :loading="savingGeneral"
                  :disabled="!generalDirty || Boolean(nameError)"
                  @click="saveGeneral"
                >
                  Save
                </gaia-button>
              </n-flex>
            </template>
          </gaia-card>

          <gaia-card>
            <template #header>Signing</template>
            <gaia-form-field label="Default signing provider">
              <gaia-select
                :value="providerValue"
                :options="providerOptions"
                placeholder="No default"
                clearable
                :loading="savingProvider"
                :disabled="savingProvider || !availableProviders.length"
                style="width: 100%; max-width: 360px"
                @update:value="changeProvider"
              />
            </gaia-form-field>
            <gaia-text
              size="meta"
              tone="subtle"
              :style="{ display: 'block', marginTop: '8px', lineHeight: 1.5 }"
            >
              <template v-if="availableProviders.length">
                Preselected whenever someone sends a document for signing. Without a default, the
                first connected provider is preselected instead.
              </template>
              <template v-else>
                No signing provider is available yet. Connect YouSign in
                <gaia-link :to="{ name: 'service-account-token' }">{{
                  navLabels.integrations
                }}</gaia-link>
                to send documents for signing.
              </template>
            </gaia-text>
            <template v-if="availableProviders.length">
              <gaia-form-field label="Default expiry" :style="{ marginTop: '16px' }">
                <!-- Keyed on the saved value: a typed-in value is kept by the select as an
                     option of its own, and once saved it is also in ours — remounting drops
                     the select's copy, so the value is listed once, in order. -->
                <gaia-select
                  :key="organisation.signing_expiration_days"
                  :value="organisation.signing_expiration_days"
                  :options="expirationOptions"
                  filterable
                  tag
                  :on-create="createExpirationOption"
                  placeholder="Pick or type a number of days"
                  :loading="savingExpiration"
                  :disabled="savingExpiration"
                  style="width: 100%; max-width: 360px"
                  @update:value="changeExpirationDays"
                />
              </gaia-form-field>
              <gaia-text
                size="meta"
                tone="subtle"
                :style="{ display: 'block', marginTop: '8px', lineHeight: 1.5 }"
              >
                New signing requests expire this long after they are sent. The sender can still
                change it per request, up to {{ MAX_EXPIRY_DAYS_AHEAD }} days — the signing
                provider's one-year limit.
              </gaia-text>
              <!-- Saved before the limit existed: requests are already held to it, so say so. -->
              <gaia-text
                v-if="organisation.signing_expiration_days > MAX_EXPIRY_DAYS_AHEAD"
                size="meta"
                tone="warning"
                :style="{ display: 'block', marginTop: '4px', lineHeight: 1.5 }"
              >
                {{ organisation.signing_expiration_days }} days is over the limit, so new requests
                use {{ MAX_EXPIRY_DAYS_AHEAD }}. Pick a shorter default to match.
              </gaia-text>
            </template>
          </gaia-card>
        </n-flex>
      </n-spin>
    </n-flex>
  </Layout>
</template>
